import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Append indexed array params (Laravel-style): key[0], key[1], ...
const appendIndexedArray = (queryParams, baseKey, arr) => {
  if (!Array.isArray(arr)) return;
  let i = 0;
  arr.forEach((val) => {
    if (val === undefined || val === null || String(val).trim() === "") return;
    queryParams.append(`${baseKey}[${i}]`, String(val));
    i += 1;
  });
};

const appendIndexedFormData = (formData, baseKey, arr) => {
  const list = Array.isArray(arr)
    ? arr
    : arr != null && String(arr).trim() !== ""
      ? [arr]
      : [];
  let i = 0;
  list.forEach((val) => {
    if (val === undefined || val === null || String(val).trim() === "") return;
    formData.append(`${baseKey}[${i}]`, String(val));
    i += 1;
  });
};

// Fetch winch partners with filters
export const fetchWinchPartners = createAsyncThunk(
  "winch_partners/fetchAll",
  async ({ page = 1, filters = {} } = {}, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);

      const add = (key, val) => {
        if (val === undefined || val === null) return;
        if (typeof val === "string" && val.trim() === "") return;
        queryParams.append(key, String(val).trim());
      };

      add("id", filters.id);
      add(
        "unified_number_or_commercial_registration_number",
        filters.unified_number_or_commercial_registration_number
      );
      add("status", filters.status);
      add("payment_method", filters.payment_method);
      add("availability", filters.availability);
      add("partner_type", filters.partner_type);
      add("city_id", filters.city_id);
      add("date_filter", filters.date_filter);

      if (filters.date_filter === "range") {
        add("date_value[0]", filters.date_value_from);
        add("date_value[1]", filters.date_value_to);
      }

      appendIndexedArray(queryParams, "tax_number", filters.tax_number);
      appendIndexedArray(queryParams, "service_ids", filters.service_ids);
      appendIndexedArray(queryParams, "vehicle_ids", filters.vehicle_ids);

      // Legacy single-value filters (still supported if passed)
      if (filters.name) queryParams.append("name", filters.name);
      if (filters.unified_number)
        queryParams.append("unified_or_cr", filters.unified_number);
      if (filters.service_id) queryParams.append("service_id", filters.service_id);
      if (filters.vehicle_id) queryParams.append("vehicle_id", filters.vehicle_id);
      if (filters.type) queryParams.append("type", filters.type);

      const url = `${BASE_URL}/partners${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
          
        },
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      return rejectWithValue(message);
    }
  }
);

// Get winch partner details
export const fetchWinchPartnerDetails = createAsyncThunk(
  "winch_partners/fetchDetails",
  async (id, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/partners/${id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partner details";
      return rejectWithValue(message);
    }
  }
);

// Delete winch partner
export const deleteWinchPartner = createAsyncThunk(
  "winch_partners/delete",
  async (id, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.delete(`${BASE_URL}/partners/${id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete winch partner";
      return rejectWithValue({ message });
    }
  }
);

// Update winch partner
export const updateWinchPartner = createAsyncThunk(
  "winch_partners/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.post(`${BASE_URL}/partners/update/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update winch partner";
      return rejectWithValue(message);
    }
  }
);

// Update winch partner financial settings (multipart FormData)
export const updateWinchPartnerFinancialSettings = createAsyncThunk(
  "winch_partners/updateFinancialSettings",
  async (formData, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.post(
        `${BASE_URL}/partners/update-financial-settings`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to update financial settings";
      return rejectWithValue(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }
);

// Get partner drivers
export const fetchPartnerDrivers = createAsyncThunk(
  "winch_partners/fetchDrivers",
  async ({ partnerId, page = 1, filters = {} } = {}, { rejectWithValue }) => {
    let user = JSON.parse(localStorage.getItem("userElwnsh"));

    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(page));
      const add = (key, val) => {
        if (val === undefined || val === null || String(val).trim() === "") return;
        queryParams.append(key, String(val).trim());
      };
      add("driver_id", filters.driver_id);
      add("provider_id", filters.driver_id);
      add("vehicle_id", filters.vehicle_id);
      appendIndexedArray(queryParams, "categories", filters.categories);
      appendIndexedArray(queryParams, "statuses", filters.statuses);
      appendIndexedArray(queryParams, "connections", filters.connections);
      if (Array.isArray(filters.categories) && filters.categories.length === 1) {
        add("service_type", filters.categories[0]);
      }
      if (Array.isArray(filters.statuses) && filters.statuses.length === 1) {
        add("status", filters.statuses[0]);
      }
      if (Array.isArray(filters.connections) && filters.connections.length === 1) {
        add("availability", filters.connections[0]);
      }

      const response = await axios.get(
        `${BASE_URL}/partners-drivers/${partnerId}?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch partner drivers";
      return rejectWithValue(message);
    }
  }
);

// Add partner drivers
export const addPartnerDrivers = createAsyncThunk(
  "winch_partners/addDrivers",
  async ({ partnerId, driverIds }, { rejectWithValue }) => {
    try {
       let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const formData = new FormData();
      formData.append('winch_partner_id', partnerId);
      driverIds.forEach(id => formData.append('driver_id[]', id));

      const response = await axios.post(`${BASE_URL}/partners-drivers`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add partner drivers";
      return rejectWithValue(message);
    }
  }
);

export const deletePartnerDriver = createAsyncThunk(
  "winch_partners/deleteDriver",
  async ({ partnerId, driverId }, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const formData = new FormData();
      formData.append("winch_partner_id", String(partnerId ?? ""));
      formData.append("driver_id", String(driverId ?? ""));
      const response = await axios.post(`${BASE_URL}/partners-drivers/delete`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete partner driver";
      return rejectWithValue({ message });
    }
  }
);

// Fetch partner services and cities
export const fetchPartnerServicesCities = createAsyncThunk(
  "winch_partners/fetchServicesCities",
  async (partnerId, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/partners/services-cities/${partnerId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch services and cities";
      return rejectWithValue(message);
    }
  }
);

// Add partner services and cities
export const addPartnerServicesCities = createAsyncThunk(
  "winch_partners/addServicesCities",
  async ({ partnerId, services, loadingCities, unloadingCities }, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const formData = new FormData();
      formData.append("partner_id", partnerId);
      appendIndexedFormData(formData, "services", services);
      appendIndexedFormData(formData, "loading_cities", loadingCities);
      appendIndexedFormData(formData, "unload_cities", unloadingCities);

      const response = await axios.post(`${BASE_URL}/partners/services-cities`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add services and cities";
      return rejectWithValue(message);
    }
  }
);

// Delete partner services and cities
export const deletePartnerServicesCities = createAsyncThunk(
  "winch_partners/deleteServicesCities",
  async (winchPartnerCityId, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const formData = new FormData();
      formData.append('winch_partner_city_id', winchPartnerCityId);
      const response = await axios.post(`${BASE_URL}/services-cities/delete`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete services and cities";
      return rejectWithValue({ message });
    }
  }
);

// Get partner vehicles
export const fetchPartnerVehicles = createAsyncThunk(
  "winch_partners/fetchVehicles",
  async ({ partnerId, page = 1, filters = {} } = {}, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(page));
      const add = (key, val) => {
        if (val === undefined || val === null || String(val).trim() === "") return;
        queryParams.append(key, String(val).trim());
      };
      add("vehicle_id", filters.vehicle_id);
      add("driver_id", filters.driver_id);
      add("provider_id", filters.driver_id);
      add("moi", filters.moi);
      appendIndexedArray(queryParams, "category_id", filters.categories);
      appendIndexedArray(queryParams, "categories", filters.categories);
      appendIndexedArray(queryParams, "plate_type", filters.plate_types);
      if (Array.isArray(filters.categories) && filters.categories.length === 1) {
        add("service_type", filters.categories[0]);
      }
      if (Array.isArray(filters.plate_types) && filters.plate_types.length === 1) {
        add("plate_type_id", filters.plate_types[0]);
      }

      const response = await axios.get(
        `${BASE_URL}/partners-vehicles/${partnerId}?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch partner vehicles";
      return rejectWithValue(message);
    }
  }
);

// Add partner vehicles
export const addPartnerVehicles = createAsyncThunk(
  "winch_partners/addVehicles",
  async ({ partnerId, vehicleIds }, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const formData = new FormData();
      formData.append('winch_partner_id', partnerId);
      vehicleIds.forEach(id => formData.append('vehicle_id[]', id));

      const response = await axios.post(`${BASE_URL}/partners-vehicles`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add partner vehicles";
      return rejectWithValue(message);
    }
  }
);

// Delete partner vehicle
export const deletePartnerVehicle = createAsyncThunk(
  "winch_partners/deleteVehicle",
  async (partnerId, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const formData = new FormData();
      formData.append('id', partnerId);
      const response = await axios.post(`${BASE_URL}/partners-vehicles/delete`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete partner vehicle";
      return rejectWithValue({ message });
    }
  }
);

// Fetch activities
export const fetchActivities = createAsyncThunk(
  "winch_partners/fetchActivities",
  async (_, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/settings/activities`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load activities";
      return rejectWithValue(message);
    }
  }
);

// Fetch winch partner users with filters
export const fetchWinchPartnerUsers = createAsyncThunk(
  "winch_partners/fetchUsers",
  async ({ page = 1, filters = {} } = {}, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);

      // Add filters to query params
      if (Array.isArray(filters.winch_partner_ids)) {
        appendIndexedArray(queryParams, "winch_partner_ids", filters.winch_partner_ids);
      } else if (filters.winch_partner_ids) {
        queryParams.append("winch_partner_ids", filters.winch_partner_ids);
      } else if (filters.winch_partner_id) {
        // Backward compatibility
        queryParams.append("winch_partner_ids", filters.winch_partner_id);
      }
      if (filters.user_id) {
        queryParams.append("user_id", filters.user_id);
      }
      if (filters.status !== undefined && filters.status !== "") {
        queryParams.append("status", filters.status);
      }
      if (filters.email) {
        queryParams.append("email", filters.email);
      }
      if (filters.date_filter) {
        queryParams.append("date_filter", filters.date_filter);
        if (filters.date_filter === "range" && Array.isArray(filters.date_value) && filters.date_value.length === 2) {
          if (filters.date_value[0]) queryParams.append("date_value[0]", filters.date_value[0]);
          if (filters.date_value[1]) queryParams.append("date_value[1]", filters.date_value[1]);
        } else if (filters.date_value && Array.isArray(filters.date_value) && filters.date_value[0] && filters.date_filter !== "range") {
          queryParams.append("date_value", filters.date_value[0]);
        }
      }

      const url = `${BASE_URL}/partner-users${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partner users";
      return rejectWithValue(message);
    }
  }
);

// Fetch all winch partner users for dropdown (without filters)
export const fetchAllWinchPartnerUsers = createAsyncThunk(
  "winch_partners/fetchAllUsers",
  async ({ page = 1 } = {}, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/partner-users?page=${page}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load users";
      return rejectWithValue(message);
    }
  }
);

// Fetch winch partner user details
export const fetchWinchPartnerUserDetails = createAsyncThunk(
  "winch_partners/fetchUserDetails",
  async (id, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/partner-users/${id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partner user details";
      return rejectWithValue(message);
    }
  }
);

// Create winch partner user
export const createWinchPartnerUser = createAsyncThunk(
  "winch_partners/createUser",
  async (formData, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.post(`${BASE_URL}/partner-users`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create user";
      return rejectWithValue(message);
    }
  }
);

// Update winch partner user
export const updateWinchPartnerUser = createAsyncThunk(
  "winch_partners/updateUser",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.post(`${BASE_URL}/partner-users/update`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update user";
      return rejectWithValue(message);
    }
  }
);

// Fetch cities for dropdown
export const fetchCities = createAsyncThunk(
  "winch_partners/fetchCities",
  async (_, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/cities`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load cities";
      return rejectWithValue(message);
    }
  }
);

// Fetch service types for dropdown
export const fetchServiceTypes = createAsyncThunk(
  "winch_partners/fetchServiceTypes",
  async (_, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/service-types`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load service types";
      return rejectWithValue(message);
    }
  }
);

// Paginated winch partners for react-select dropdowns
export const fetchWinchPartnersForSelect = createAsyncThunk(
  "winch_partners/fetchForSelect",
  async (params = {}, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem("userElwnsh"));
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20 } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) queryParams.set("search", String(search));
      const response = await axios.get(`${BASE_URL}/partners?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      return rejectWithValue(message);
    }
  }
);

// Fetch all drivers for dropdown (paginated + search)
export const fetchAllDrivers = createAsyncThunk(
  "winch_partners/fetchAllDrivers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem("userElwnsh"));
      const queryParams = new URLSearchParams();
      const { search, name, page = 1, per_page = 20 } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      const searchTerm = String(name || search || "").trim();
      if (searchTerm) queryParams.set("name", searchTerm);
      const response = await axios.get(`${BASE_URL}/drivers?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load drivers";
      return rejectWithValue(message);
    }
  }
);

// Fetch vehicles for dropdown (paginated + search)
export const fetchVehicles = createAsyncThunk(
  "winch_partners/fetchAllVehicles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem("userElwnsh"));
      const queryParams = new URLSearchParams();
      const { search, page = 1, per_page = 20 } = params;
      queryParams.set("page", String(page));
      queryParams.set("per_page", String(per_page));
      if (search) queryParams.set("search", String(search));
      const response = await axios.get(
        `${BASE_URL}/settings/vehicles?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicles";
      return rejectWithValue(message);
    }
  }
);

// Fetch winch partner user permissions
export const fetchWinchPartnerPermissions = createAsyncThunk(
  "winch_partners/fetchPermissions",
  async (page = 1, { rejectWithValue }) => {
    try {
      let user = JSON.parse(localStorage.getItem("userElwnsh"));
      const response = await axios.get(`${BASE_URL}/partners/users-permissions?page=${page}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          "Lang": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load permissions";
      return rejectWithValue(message);
    }
  }
);

const WinchPartnersSlice = createSlice({
  name: "winch_partners",
  initialState: {
    winch_partners: [],
    partnerDetails: null,
    partnerDrivers: { data: [], pagination: null },
    partnerVehicles: { data: [], pagination: null },
    partnerServicesCities: [],
    cities: [],
    serviceTypes: [],
    vehicles: [],
    drivers: [],
    allDrivers: [],
    allDriversPagination: null,
    allDriversLoading: false,
    selectPartners: [],
    selectPartnersPagination: null,
    selectPartnersLoading: false,
    vehiclesDropdownPagination: null,
    vehiclesDropdownLoading: false,
    activities: [],
    winchPartnerUsers: [],
    winchPartnerUserDetails: null,
    usersPagination: null,
    permissions: [],
    permissionsPagination: null,
    isLoadingPermissions: false,
    isLoading: false,
    isLoadingDetails: false,
    isLoadingDrivers: false,
    isLoadingVehicles: false,
    isLoadingFinancialSettings: false,
    isLoadingUsers: false,
    isLoadingUserDetails: false,
    error: null,
    success: null,
    financialSettingsSuccess: null,
    financialSettingsError: null,
    errorDelete: null,
    successDelete: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.errorDelete = null;
      state.successDelete = null;
      state.financialSettingsSuccess = null;
      state.financialSettingsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch winch partners
      .addCase(fetchWinchPartners.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWinchPartners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winch_partners = action?.payload?.data?.data || action?.payload?.data || [];
        state.pagination = action?.payload?.data?.pagination || action?.payload?.pagination || null;
      })
      .addCase(fetchWinchPartners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load winch partners";
      })

      // Fetch partner details
      .addCase(fetchWinchPartnerDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.error = null;
      })
      .addCase(fetchWinchPartnerDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        state.partnerDetails = action.payload.data;
      })
      .addCase(fetchWinchPartnerDetails.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.error = action.payload || "Failed to load partner details";
      })

      // Delete winch partner
      .addCase(deleteWinchPartner.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteWinchPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove from data array
        if (state.winch_partners) {
          state.winch_partners = state.winch_partners.filter(
            (record) => record.id !== action.meta.arg
          );
        }
        state.successDelete = action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteWinchPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete = action.payload?.message || "Failed to delete winch partner";
      })

      // Update winch partner
      .addCase(updateWinchPartner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWinchPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
        state.partnerDetails = action.payload.data;
      })
      .addCase(updateWinchPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update partner";
      })

      .addCase(updateWinchPartnerFinancialSettings.pending, (state) => {
        state.isLoadingFinancialSettings = true;
        state.financialSettingsError = null;
      })
      .addCase(updateWinchPartnerFinancialSettings.fulfilled, (state, action) => {
        state.isLoadingFinancialSettings = false;
        state.financialSettingsSuccess =
          action.payload?.message || action.payload?.data?.message || "OK";
        if (action.payload?.data) {
          state.partnerDetails = action.payload.data;
        }
      })
      .addCase(updateWinchPartnerFinancialSettings.rejected, (state, action) => {
        state.isLoadingFinancialSettings = false;
        state.financialSettingsError = action.payload || "Failed to update financial settings";
      })

      // Fetch partner drivers
      .addCase(fetchPartnerDrivers.pending, (state) => {
        state.isLoadingDrivers = true;
        state.error = null;
      })
      .addCase(fetchPartnerDrivers.fulfilled, (state, action) => {
        state.isLoadingDrivers = false;
        state.partnerDrivers = {
          data: action.payload?.data?.data || action.payload?.data || [],
          pagination: action.payload?.data?.pagination || action.payload?.pagination || null,
        };
      })
      .addCase(fetchPartnerDrivers.rejected, (state, action) => {
        state.isLoadingDrivers = false;
        state.error = action.payload || "Failed to fetch partner drivers";
      })

      // Add partner drivers
      .addCase(addPartnerDrivers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPartnerDrivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addPartnerDrivers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add partner drivers";
      })

      // Delete partner driver
      .addCase(deletePartnerDriver.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePartnerDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successDelete = action.payload?.message || "Driver deleted successfully";
      })
      .addCase(deletePartnerDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete = action.payload?.message || "Failed to delete driver";
      })

      // Fetch partner services and cities
      .addCase(fetchPartnerServicesCities.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchPartnerServicesCities.fulfilled, (state, action) => {
        state.partnerServicesCities = action.payload?.data || [];
      })
      .addCase(fetchPartnerServicesCities.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch services and cities";
      })

      // Add services and cities
      .addCase(addPartnerServicesCities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPartnerServicesCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addPartnerServicesCities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add services and cities";
      })

      // Delete partner services and cities
      .addCase(deletePartnerServicesCities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePartnerServicesCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successDelete = action.payload?.message || "Services and cities deleted successfully";
      })
      .addCase(deletePartnerServicesCities.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete = action.payload?.message || "Failed to delete services and cities";
      })

      // Fetch partner vehicles
      .addCase(fetchPartnerVehicles.pending, (state) => {
        state.isLoadingVehicles = true;
        state.error = null;
      })
      .addCase(fetchPartnerVehicles.fulfilled, (state, action) => {
        state.isLoadingVehicles = false;
        const root = action.payload?.data ?? action.payload;
        const rows = Array.isArray(root?.data)
          ? root.data
          : Array.isArray(root)
            ? root
            : [];
        state.partnerVehicles = {
          data: rows,
          pagination: root?.pagination || action.payload?.pagination || null,
        };
      })
      .addCase(fetchPartnerVehicles.rejected, (state, action) => {
        state.isLoadingVehicles = false;
        state.error = action.payload || "Failed to fetch partner vehicles";
      })

      // Add partner vehicles
      .addCase(addPartnerVehicles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPartnerVehicles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addPartnerVehicles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add partner vehicles";
      })

      // Delete partner vehicle
      .addCase(deletePartnerVehicle.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePartnerVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successDelete = action.payload?.message || "Vehicle deleted successfully";
      })
      .addCase(deletePartnerVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete = action.payload?.message || "Failed to delete vehicle";
      })

      // Fetch cities
      .addCase(fetchCities.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cities = action.payload.data?.data || action.payload.data || [];
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.error = action.payload || "Failed to load cities";
      })

      // Fetch service types
      .addCase(fetchServiceTypes.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchServiceTypes.fulfilled, (state, action) => {
        state.serviceTypes = action.payload.data?.data || action.payload.data || [];
      })
      .addCase(fetchServiceTypes.rejected, (state, action) => {
        state.error = action.payload || "Failed to load service types";
      })

      // Paginated partners for select dropdowns
      .addCase(fetchWinchPartnersForSelect.pending, (state) => {
        state.selectPartnersLoading = true;
      })
      .addCase(fetchWinchPartnersForSelect.fulfilled, (state, action) => {
        state.selectPartnersLoading = false;
        const newData = action.payload?.data || [];
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          state.selectPartners = newData;
        } else {
          const prev = Array.isArray(state.selectPartners) ? state.selectPartners : [];
          const ids = new Set(prev.map((r) => r.id));
          state.selectPartners = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.selectPartnersPagination = action.payload?.pagination || null;
      })
      .addCase(fetchWinchPartnersForSelect.rejected, (state, action) => {
        state.selectPartnersLoading = false;
        state.error = action.payload || "Failed to load winch partners";
      })

      // Fetch all drivers
      .addCase(fetchAllDrivers.pending, (state) => {
        state.allDriversLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDrivers.fulfilled, (state, action) => {
        state.allDriversLoading = false;
        const newData = action.payload?.data || [];
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          state.allDrivers = newData;
        } else {
          const prev = Array.isArray(state.allDrivers) ? state.allDrivers : [];
          const ids = new Set(prev.map((r) => r.id));
          state.allDrivers = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.allDriversPagination = action.payload?.pagination || null;
      })
      .addCase(fetchAllDrivers.rejected, (state, action) => {
        state.allDriversLoading = false;
        state.error = action.payload || "Failed to load drivers";
      })

      // Fetch all vehicles
      .addCase(fetchVehicles.pending, (state) => {
        state.vehiclesDropdownLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.vehiclesDropdownLoading = false;
        const newData = action.payload?.data || [];
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          state.vehicles = newData;
        } else {
          const prev = Array.isArray(state.vehicles) ? state.vehicles : [];
          const ids = new Set(prev.map((r) => r.id));
          state.vehicles = [
            ...prev,
            ...newData.filter((r) => r?.id != null && !ids.has(r.id)),
          ];
        }
        state.vehiclesDropdownPagination = action.payload?.pagination || null;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.vehiclesDropdownLoading = false;
        state.error = action.payload || "Failed to load vehicles";
      })

      // Fetch activities
      .addCase(fetchActivities.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.activities = action.payload.data || [];
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.error = action.payload || "Failed to load activities";
      })

      // Fetch winch partner users
      .addCase(fetchWinchPartnerUsers.pending, (state) => {
        state.isLoadingUsers = true;
        state.error = null;
      })
      .addCase(fetchWinchPartnerUsers.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        state.winchPartnerUsers = action.payload?.data?.data || [];
        state.usersPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchWinchPartnerUsers.rejected, (state, action) => {
        state.isLoadingUsers = false;
        state.error = action.payload || "Failed to load winch partner users";
      })

      // Fetch winch partner user details
      .addCase(fetchWinchPartnerUserDetails.pending, (state) => {
        state.isLoadingUserDetails = true;
        state.winchPartnerUserDetails = null;
        state.error = null;
      })
      .addCase(fetchWinchPartnerUserDetails.fulfilled, (state, action) => {
        state.isLoadingUserDetails = false;
        state.winchPartnerUserDetails = action.payload?.data || null;
      })
      .addCase(fetchWinchPartnerUserDetails.rejected, (state, action) => {
        state.isLoadingUserDetails = false;
        state.error = action.payload || "Failed to load winch partner user details";
      })

      // Create winch partner user
      .addCase(createWinchPartnerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createWinchPartnerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "User created successfully";
      })
      .addCase(createWinchPartnerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create user";
      })

      // Update winch partner user
      .addCase(updateWinchPartnerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWinchPartnerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "User updated successfully";
      })
      .addCase(updateWinchPartnerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update user";
      })

      // Fetch winch partner user permissions
      .addCase(fetchWinchPartnerPermissions.pending, (state) => {
        state.isLoadingPermissions = true;
        state.error = null;
      })
      .addCase(fetchWinchPartnerPermissions.fulfilled, (state, action) => {
        state.isLoadingPermissions = false;
        state.permissions = action.payload?.data?.data || [];
        state.permissionsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchWinchPartnerPermissions.rejected, (state, action) => {
        state.isLoadingPermissions = false;
        state.error = action.payload || "Failed to load permissions";
      })

      // Fetch all winch partner users for dropdown
      .addCase(fetchAllWinchPartnerUsers.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAllWinchPartnerUsers.fulfilled, (state, action) => {
        const users = action.payload?.data?.data || action.payload?.data || [];
        // Append to existing users if pagination exists
        if (action.meta.arg?.page === 1 || !action.meta.arg) {
          state.allWinchPartnerUsers = users;
        } else {
          state.allWinchPartnerUsers = [...state.allWinchPartnerUsers, ...users];
        }
      })
      .addCase(fetchAllWinchPartnerUsers.rejected, (state, action) => {
        state.error = action.payload || "Failed to load users";
      });
  },
});

export const { clearState } = WinchPartnersSlice.actions;
export default WinchPartnersSlice.reducer;